import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { CircularProgress, LinearProgress } from '@material-ui/core';
import { blues } from '@/styles/colors';
import { RESYNC_OVERLAY_ATTRIBUTE } from '../hooks/useUserStarted';

/** How long the open-time check runs before its progress bar shows, so fast opens don't flicker. */
const PROGRESS_DELAY_MS = 300;

/**
 * Covers the tab rail and form body while the modal loads the latest version of the order. It
 * takes focus and announces its text. Render it inside a positioned container, next to the body
 * it covers (not inside it), so the body can be made inert without disabling the overlay.
 */
const ResyncOverlay = () => {
    const ref = useRef(null);

    useEffect(() => {
        ref.current?.focus();
    }, []);

    return (
        <div
            ref={ref}
            {...{ [RESYNC_OVERLAY_ATTRIBUTE]: '' }}
            role="status"
            aria-live="polite"
            tabIndex={-1}
            css={css`
                position: absolute;
                inset: 0;
                z-index: 2;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 8px;
                background: rgba(255, 255, 255, 0.8);
                text-align: center;
                outline: none;
            `}
        >
            <CircularProgress css={css({ color: blues.primary })} />
            <div>Loading the latest version of this order…</div>
            <div
                css={css`
                    font-size: 0.875rem;
                    color: rgba(0, 0, 0, 0.6);
                `}
            >
                It changed after this page loaded.
            </div>
        </div>
    );
};

/**
 * Thin indeterminate bar for the open-time version check. It appears only if the check is still
 * running after 300 ms. Mount it only while the check runs.
 */
export const CheckingProgress = () => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setVisible(true), PROGRESS_DELAY_MS);
        return () => clearTimeout(timer);
    }, []);

    if (!visible) return null;
    return <LinearProgress aria-label="Checking for updates" css={css({ flexShrink: 0 })} />;
};

export default ResyncOverlay;
